<template>
    <div class="freelancer-filter / filter-content" :class="{isMobileScreen, isProjectListTabletScreen, isProjectListDesktopScreen}">
        <label v-if="!isProjectListDesktopScreen" class="filter-content_small-screen-label">{{ label }}</label>
        <div class="filter-content_head / flex">
            <div v-if="displaySearch" class="filter-content_head_input">
                <VJoyInput
                    :model-value="searchModelValue"
                    icon="search"
                    :placeholder="searchPlaceholder"
                    name="search"
                    size="small"
                    @update:model-value="handleSearch"
                />
                <VJoySpinner v-if="pending" />
            </div>

            <div v-if="isProjectListDesktopScreen" class="filter-content_head_reset flex">
                <GhostButton
                    :circle="displaySearch"
                    class="filter-content_head_reset_button"
                    size="s"
                    :disabled="isResetDisabled"
                    background
                    :title="t('projectclient.projectlist.topbar.button.reset')"
                    @click="handleReset"
                >
                    <VJoyIcon v-if="displaySearch" name="cross-circle" :color="isResetDisabled ? 'neutral' : 'secondary'" circle size="xsmall" />
                    <span v-else>{{ t('projectclient.projectlist.topbar.button.reset') }}</span>
                </GhostButton>
            </div>
        </div>

        <div v-if="slots.default" class="filter-content_list / flex">
            <slot />
        </div>
        <template v-else-if="slots.item">
            <div v-if="items?.length" class="filter-content_list / flex">
                <VJoyCheckbox
                    v-for="item of orderedItem"
                    :key="item.value"
                    :checked="selectedItems?.includes(item.value)"
                    :value="item.value"
                    @update:checked="emit('check:item', item)"
                >
                    <div class="ellipsis" :title="item.label">
                        <slot name="item" :item="item" />
                    </div>
                </VJoyCheckbox>
            </div>
            <span v-else class="filter-content_empty">
                {{ t('projectclient.projectlist.topbar.filters.list.empty') }}
            </span>
        </template>
        <GhostButton v-if="displayShowMore" background size="m" class="filter-content_show-more-button" @click="emit('show-more')">
            {{ t('projectclient.projectlist.topbar.button.showMore') }}
        </GhostButton>
    </div>
</template>

<script setup lang="ts" generic="T extends TypedAggregationResource<any>">
    import {computed, type VNode} from 'vue';
    import type {TypedAggregationResource} from '@common/composables';
    import {useScreenSizeStore} from '@common/stores';
    import {GhostButton} from '@common/ui';
    import debounce from 'lodash-es/debounce';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyCheckbox, VJoyIcon, VJoyInput, VJoySpinner} from '@maltjoy/core-vue';

    const {t} = useTranslation();

    interface Props {
        label: string;
        displayReset?: boolean;
        displaySearch?: boolean;
        searchModelValue?: string;
        searchPlaceholder?: string;
        items?: T[];
        selectedItems?: T['value'][];
        displayShowMore?: boolean;
        pending?: boolean;
    }

    const props = defineProps<Props>();

    const emit = defineEmits<{
        'update:search-model-value': [value: string];
        reset: [];
        'check:item': [value: T];
        'show-more': [];
    }>();

    const slots = defineSlots<{
        default?(): VNode;
        item?(props: {item: T}): VNode;
    }>();

    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen, isProjectListTabletScreen, isProjectListDesktopScreen} = storeToRefs(screenSizeStore);

    const handleSearch = debounce((value) => {
        emit('update:search-model-value', value);
    }, 100);

    function handleReset() {
        emit('reset');
        emit('update:search-model-value', '');
    }

    const orderedItem = computed(() => props?.items?.toSorted((a, b) => a.order - b.order));

    const isResetDisabled = computed(() => {
        if (props.items) {
            return !props.selectedItems?.length && !props.searchModelValue?.length;
        } else {
            return !props.displayReset;
        }
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .filter-content {
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-4) 0 var(--joy-core-spacing-4);
        display: flex;
        flex-flow: column nowrap;

        $parent: &;

        &_list {
            flex-flow: column nowrap;

            :deep(.joy-checkbox__input-wrapper) {
                margin: 0;
            }

            :deep(.joy-checkbox__content-wrapper) {
                flex-wrap: nowrap;
                flex: 1 1 auto;
                min-width: 0;
            }

            :deep(.joy-checkbox) {
                flex-flow: row nowrap;
                gap: var(--joy-core-spacing-2);
                min-width: 0;
            }

            :deep(.joy-checkbox__content) {
                width: 0 !important;
                flex-basis: 0 !important;
            }
        }

        &.isMobileScreen {
            padding: var(--joy-core-spacing-4) 0 0 0;

            #{$parent}_list {
                grid-template-columns: minmax(200px, 1fr);
                gap: var(--joy-core-spacing-2);
            }

            :deep(.joy-input--medium) {
                margin: var(--joy-core-spacing-4) var(--joy-core-spacing-5) var(--joy-core-spacing-4) 0;
            }
        }

        &.isProjectListTabletScreen {
            padding: var(--joy-core-spacing-4) 0 0 0;

            #{$parent}_list {
                display: grid;
                grid-template-columns: repeat(2, minmax(200px, 1fr));
                gap: var(--joy-core-spacing-1);
                grid-auto-rows: max-content;

                joy-checkbox {
                    min-width: 250px;
                }
            }

            :deep(.joy-input--medium) {
                margin: var(--joy-core-spacing-4) var(--joy-core-spacing-8) var(--joy-core-spacing-4) 0;
            }
        }

        &_head {
            flex-flow: row nowrap;
            gap: var(--joy-core-spacing-3);

            &_input {
                position: relative;
                margin-bottom: var(--joy-core-spacing-4);
                flex: 1 1 auto;

                :deep(.joy-spinner) {
                    --spinner-size: 20px;

                    position: absolute;
                    right: var(--joy-core-spacing-3);
                    top: 10px;
                    z-index: 10;
                }

                :deep(.joy-input--wrapper) {
                    margin-bottom: 0;
                }
            }

            &_reset {
                flex: 0 0 auto;
                align-items: center;

                &_button {
                    margin-bottom: var(--joy-core-spacing-4);
                    font-size: var(--joy-font-size-primary-200);
                }
            }
        }

        &_small-screen-label {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-600);
            margin-bottom: var(--joy-core-spacing-4);
        }

        &_empty {
            font-size: var(--joy-font-size-primary-200);
            padding: var(--joy-core-spacing-2) 0;
            margin-bottom: var(--joy-core-spacing-4);
        }

        &_show-more-button {
            width: fit-content;
            align-self: center;
            margin: var(--joy-core-spacing-2) 0 var(--joy-core-spacing-4) 0;
        }

        &:not(.isProjectListDesktopScreen, :last-child) {
            border-bottom: 1px solid var(--joy-color-neutral-30);
            margin-bottom: var(--joy-core-spacing-4);
            padding-bottom: var(--joy-core-spacing-4);
        }
    }
</style>
